import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { Crown, Flame, Award, Target, TrendingUp, Trophy } from "lucide-react";

export default function CareerRecords() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.career().then(setData);
  }, []);

  if (!data) return <div className="font-display text-2xl" data-testid="career-loading">Lade Rekorde...</div>;

  const { records, career_table, total_matches } = data;

  return (
    <div className="space-y-8" data-testid="career-page">
      <div>
        <div className="text-xs uppercase tracking-[0.4em]" style={{ color: "var(--dc-chalk)" }}>Ewige Bestenliste</div>
        <h1 className="font-display text-5xl">Career Records</h1>
        <div className="text-sm font-mono-data mt-2" style={{ color: "var(--dc-muted)" }}>
          Alle Saisons · <span className="text-white">{total_matches}</span> gespielte Partien
        </div>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <RecordCard icon={TrendingUp} label="Bestes Match-Ø" value={records.best_match_average.value} name={records.best_match_average.player_name} color="var(--dc-secondary)" testid="record-avg" />
        <RecordCard icon={Award} label="Highest Finish" value={records.highest_finish.value} name={records.highest_finish.player_name} color="var(--dc-chalk)" textDark testid="record-hf" />
        <RecordCard icon={Target} label="Meiste High Finishes" value={records.most_high_finishes.value} name={records.most_high_finishes.player_name} color="var(--dc-chalk)" testid="record-hf-count" />
        <RecordCard icon={Flame} label="Meiste 180er" value={records.most_180_total.value} name={records.most_180_total.player_name} color="var(--dc-primary)" testid="record-180-total" />
        <RecordCard icon={Target} label="Meiste Bullsiege" value={records.most_ausgebullt.value} name={records.most_ausgebullt.player_name} color="var(--dc-primary)" testid="record-bull" />
        <RecordCard icon={Trophy} label="Längste Siegesserie" value={records.longest_win_streak.value} name={records.longest_win_streak.player_name} note={records.longest_win_streak.period} color="var(--dc-chalk)" testid="record-longest-streak" />
        <RecordCard icon={Flame} label="Aktuelle Siegesserie" value={records.current_win_streak.value} name={records.current_win_streak.player_name} note={records.current_win_streak.period} color="var(--dc-secondary)" testid="record-current-streak" />
      </section>

      <section className="dc-border p-6">
        <h2 className="font-display text-3xl mb-4 flex items-center gap-2"><Crown size={20} style={{ color: "var(--dc-chalk)" }} /> Ewige Rangliste</h2>
        <div className="overflow-x-auto">
          <table className="w-full font-mono-data text-sm">
            <thead style={{ color: "var(--dc-muted)" }} className="text-xs uppercase">
              <tr>
                <th className="text-left py-2 pr-2">#</th>
                <th className="text-left">Spieler</th>
                <th className="text-right px-2">Sp</th>
                <th className="text-right px-2">S</th>
                <th className="text-right px-2">N</th>
                <th className="text-right px-2">Win%</th>
                <th className="text-right px-2">Legs</th>
                <th className="text-right px-2">Avg∅</th>
                <th className="text-right px-2">Best</th>
                <th className="text-right px-2">F-Ø</th>
                <th className="text-right px-2">180</th>
                <th className="text-right px-2">HF</th>
                <th className="text-right px-2">HF #</th>
                <th className="text-right px-2">Bull</th>
              </tr>
            </thead>
            <tbody>
              {career_table.map((r, i) => (
                <tr key={r.player_id} className="row-in border-t dc-border hover:bg-[color:var(--dc-surface-hover)]" style={{ animationDelay: `${i * 30}ms` }} data-testid={`career-row-${r.player_id}`}>
                  <td className="py-2 pr-2">
                    <span className="inline-block w-6 text-center" style={{ background: i === 0 ? "var(--dc-chalk)" : "transparent", color: i === 0 ? "#000" : "inherit" }}>{i + 1}</span>
                  </td>
                  <td style={{ fontFamily: "Manrope" }}><Link to={`/spieler/${r.player_id}`} className="hover:underline">{r.name}</Link></td>
                  <td className="text-right px-2">{r.matches}</td>
                  <td className="text-right px-2" style={{ color: "var(--dc-secondary)" }}>{r.wins}</td>
                  <td className="text-right px-2" style={{ color: "var(--dc-primary)" }}>{r.losses}</td>
                  <td className="text-right px-2">{r.win_rate}%</td>
                  <td className="text-right px-2">{r.legs_won}/{r.legs_played}</td>
                  <td className="text-right px-2">{r.career_avg}</td>
                  <td className="text-right px-2" style={{ color: "var(--dc-secondary)" }}>{r.best_avg}</td>
                  <td className="text-right px-2">{r.finish_average || "—"}</td>
                  <td className="text-right px-2" style={{ color: "var(--dc-primary)" }}>{r.s180_total}</td>
                  <td className="text-right px-2" style={{ color: "var(--dc-chalk)" }}>{r.highest_finish}</td>
                  <td className="text-right px-2" style={{ color: "var(--dc-chalk)" }}>{r.high_finish_count}</td>
                  <td className="text-right px-2">{r.ausgebullt}</td>
                </tr>
              ))}
              {career_table.length === 0 && (
                <tr><td colSpan={14} className="py-8 text-center" style={{ color: "var(--dc-muted)" }}>Noch keine Daten.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function RecordCard({ icon: Icon, label, value, name, note, color, testid }) {
  return (
    <div className="dc-border p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest" style={{ color: "var(--dc-muted)" }}>{label}</span>
        <Icon size={16} style={{ color }} />
      </div>
      <div className="font-display text-6xl font-mono-data mt-2" style={{ color }}>{value || "—"}</div>
      <div className="text-sm mt-1">{name || "—"}</div>
      {note && <div className="text-xs mt-1" style={{ color: "var(--dc-muted)" }}>{note}</div>}
    </div>
  );
}
